/*
 * position-codecs.ts - camera definitions in the position formats the
 * public API accepts
 */

import type MapCamera from './camera';


/**
 * The vts-geospatial ten-component position: view mode, x, y, height
 * mode, height, yaw, pitch, roll, view extent, field of view. The view
 * mode is always `obj`; the height mode `fix` is an altitude from the
 * `datum` reference of `MapCamera.AltitudeRef` (the ellipsoid on a
 * geocentric frame) and `float` one from the terrain.
 */
export const vts = {

    /**
     * Decodes a position array into a camera definition. A missing or
     * non-numeric component takes its default; the pre-vts nine-slot
     * `fixed` form is accepted. A `subj` position cannot be represented
     * and is refused with a console warning.
     *
     * @returns the definition, or null when the input was refused
     */
    decode(input: readonly (number | string)[]): MapCamera.Definition | null {

        let pos = input.slice();

        // the pre-vts form has no view mode; its first slot is the
        // height mode
        if (pos[0] === 'fixed') pos = ['obj', pos[1], pos[2], 'fix',
            pos[3], pos[4], pos[5], pos[6], pos[7], pos[8]];

        if (pos[0] === 'subj') {

            console.warn('Position refused: the subj view mode is not '
                + 'supported: ' + JSON.stringify(input));
            return null;
        }

        return {
            center: [number(pos[1], 0), number(pos[2], 0)],
            altitudeRef: (pos[3] === 'fix' || pos[3] === 'fixed')
                ? 'datum' : 'terrain',
            altitude: number(pos[4], 0),
            yaw: number(pos[5], 0),
            pitch: number(pos[6], -90),
            roll: number(pos[7], 0),
            viewExtent: number(pos[8], 900),
            fovy: number(pos[9], 45),
        };
    },

    /** Encodes a camera definition as a position array. */
    encode(definition: MapCamera.Definition): vts.Position {

        return [
            'obj',
            definition.center[0],
            definition.center[1],
            definition.altitudeRef === 'datum' ? 'fix' : 'float',
            definition.altitude,
            definition.yaw,
            definition.pitch,
            definition.roll,
            definition.viewExtent,
            definition.fovy,
        ];
    },

    /**
     * Encodes a camera definition as the comma-separated position
     * string used in URLs: six decimals for the center, two elsewhere.
     */
    encodeString(definition: MapCamera.Definition): string {

        const pos = vts.encode(definition);

        return pos.map((value, index) => {

            if (typeof value === 'string') return value;
            return value.toFixed(index <= 2 ? 6 : 2);
        }).join(',');
    },
};

export namespace vts {

    /** The ten-component position array. */
    export type Position = (number | string)[];
}


/** The numeric value of a position slot, or the default. */
function number(value: number | string | undefined, fallback: number) {

    if (value == null) return fallback;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}
